/**
 * useReportsViewState — everything ReportsView knows besides its markup: which
 * screen is open (the list, a filed report, an unfiled window's preview, or an
 * older ReportEntry detail), the report contents on screen, the client-report
 * confirms, and adding and removing items while editing a filed report.
 *
 * It is one hook on purpose. The filed detail and the preview share the one
 * `sidecar` slot and the request counters that drop late answers, so splitting
 * the screens' state apart would change what a late answer can overwrite.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import {
  fileableWindowFromFiled,
  isAlreadyFiled,
  type FileableWindow,
  type FiledReport,
} from "../reports/report-client";
import { clientBlocked, curationTargetId, leavesTheCompany, occurrenceId } from "../reports/report-labels";
import type { ReportEntry, ReportIncludedItem, ReportSidecar } from "../reports/report-types";
import { CLIENT_REPORT_COMING_SOON } from "../triage/client-report-gate";
import type { ReportFilter, ReportsViewProps } from "./ReportsView";

export type ReportsViewStateDeps = Pick<
  ReportsViewProps,
  | "reports"
  | "fetchPdfBytes"
  | "fetchSidecar"
  | "fetchFiledSidecar"
  | "fetchWindowPreview"
  | "previewRefreshKey"
  | "onSetItemIncluded"
  | "onSend"
  | "onRefile"
  | "onComingSoon"
  | "projectKey"
  | "onFileWindow"
> &
  Required<Pick<ReportsViewProps, "fileable" | "filed" | "filedStatus">>;

export function useReportsViewState({
  reports,
  fetchPdfBytes,
  fetchSidecar,
  fetchFiledSidecar,
  fetchWindowPreview,
  previewRefreshKey,
  onSetItemIncluded,
  onSend,
  onRefile,
  onComingSoon,
  projectKey,
  fileable,
  filed,
  filedStatus,
  onFileWindow,
}: ReportsViewStateDeps) {
  // §4.7 LOCKED: the CLIENT row is never one tap, on this surface too. Separate
  // state from the detail screen's `confirmOpen` so the two gates can never be
  // satisfied by each other.
  const [confirmWindow, setConfirmWindow] = useState<FileableWindow | null>(null);
  const [filter, setFilter] = useState<ReportFilter>("all");
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [sidecar, setSidecar] = useState<ReportSidecar | null>(null);
  /** Set when the contents could not be READ, which must never render as a report
   * that contained nothing — "we can't say" and "it was empty" are different
   * answers, and only one of them is a reason to go look in the folder. */
  const [sidecarError, setSidecarError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // #19: the filed-report detail. Keyed by `bucket/filename` — the pair that
  // actually identifies a file in the folder.
  const [filedDetailKey, setFiledDetailKey] = useState<string | null>(null);
  /** A not-yet-filed window opened to see what would go into it. */
  const [previewWindow, setPreviewWindow] = useState<FileableWindow | null>(null);
  const [refileTarget, setRefileTarget] = useState<FiledReport | null>(null);
  const [curationBusy, setCurationBusy] = useState<string | null>(null);
  const [curationError, setCurationError] = useState<string | null>(null);
  const [removedItems, setRemovedItems] = useState<Map<string, ReportIncludedItem>>(() => new Map());
  const [selectedAdditions, setSelectedAdditions] = useState<Set<string>>(() => new Set());
  const previewWindowRef = useRef<FileableWindow | null>(null);
  const editingPreviewRef = useRef(false);
  const previewRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const rootRef = useRef<HTMLElement>(null);
  const curationGenerationRef = useRef(0);
  const curationAbortRef = useRef<AbortController | null>(null);

  /* The filter applies to EVERY list on this surface, not just the (now empty)
     ReportEntry one. It used to narrow `reports` alone, so once the fixtures were
     retired the pills became decorative: they highlighted, and nothing moved.
     A control that visibly responds while changing nothing is worse than one that
     is plainly disabled. */
  const matches = (kind: string): boolean => filter === "all" || kind === filter;
  const filtered = reports.filter((r) => matches(r.report_kind));
  /* #19: "Ready to file" is a TO-DO list, so a window that has been filed leaves
     it. It used to keep every window forever — the section never emptied, the
     same report appeared twice on one screen (once as "File again", once under
     Filed), and finished work still read as outstanding. Re-filing is still
     legitimate and still available; it just lives on the filed report's own
     detail screen now, where it is a deliberate second visit rather than the
     default-looking action on a row that looks unfinished. */
  const fileableInFilter = (filedStatus === "ready" ? fileable : []).filter((w) => matches(w.report_kind));
  const fileableShown = fileableInFilter.filter((w) => !isAlreadyFiled(w, filed));
  const filedShown = (filedStatus === "ready" ? filed : []).filter((f) => matches(f.kind));
  const detail = detailKey !== null ? reports.find((r) => r.pdf_key === detailKey) ?? null : null;
  // Resolved against the UNFILTERED list: the detail is already open by the time
  // a filter could change, and finding nothing would blank the screen.
  const filedDetail =
    filedDetailKey !== null
      ? filed.find((f) => `${f.bucket}/${f.filename}` === filedDetailKey) ?? null
      : null;

  async function openDetail(entry: ReportEntry) {
    const request = ++detailRequestRef.current;
    setDetailKey(entry.pdf_key);
    setFiledDetailKey(null); // the two details are mutually exclusive views
    setConfirmOpen(false);
    setSidecar(null);
    if (fetchSidecar) {
      const sc = await fetchSidecar(entry.sidecar_key);
      if (request !== detailRequestRef.current) return;
      setSidecar(sc);
    }
  }

  function closeDetail() {
    detailRequestRef.current += 1;
    setDetailKey(null);
    setConfirmOpen(false);
    setSidecar(null);
  }

  async function openFiledDetail(f: FiledReport) {
    const request = ++detailRequestRef.current;
    setFiledDetailKey(`${f.bucket}/${f.filename}`);
    setDetailKey(null);
    // The detail reuses the list's scroller; start it at the top, not wherever the list was.
    const scroller = rootRef.current?.closest("main");
    if (scroller) scroller.scrollTop = 0;
    // Reuse the same `sidecar` slot: the two details are mutually exclusive
    // views, so only one of them can be showing contents at a time.
    setSidecar(null);
    setSidecarError(null);
    if (!fetchFiledSidecar) return;
    const out = await fetchFiledSidecar(f);
    if (request !== detailRequestRef.current) return;
    if (out === null) setSidecarError("unavailable");
    else setSidecar(out);
  }

  function closeFiledDetail() {
    detailRequestRef.current += 1;
    setFiledDetailKey(null);
    setSidecar(null);
    setSidecarError(null);
  }

  /** Open a window that has NOT been filed and show what would go into it.
   *
   * Red, 2026-07-31: "In Foster I attached daily logs to the daily report but it
   * still doesn't let me click into today's report." Contents were only readable
   * once a report had been filed, which is backwards — the moment you want to
   * check is before committing a PDF to the folder Mercer reads. */
  async function loadPreview(w: FileableWindow) {
    const request = ++previewRequestRef.current;
    if (!fetchWindowPreview) return;
    const out = await fetchWindowPreview(w, undefined, editingPreviewRef.current);
    if (request !== previewRequestRef.current) return;
    if (out === null) setSidecarError("unavailable");
    else setSidecar(out);
  }

  function cancelCuration(): void {
    curationAbortRef.current?.abort();
    curationAbortRef.current = null;
  }

  async function openPreview(w: FileableWindow, filedReport: FiledReport | null = null) {
    cancelCuration();
    curationGenerationRef.current += 1;
    previewWindowRef.current = w;
    editingPreviewRef.current = filedReport !== null;
    setRemovedItems(new Map());
    setSelectedAdditions(new Set());
    setCurationBusy(null);
    setCurationError(null);
    setPreviewWindow(w);
    setRefileTarget(filedReport);
    setFiledDetailKey(null);
    setDetailKey(null);
    setConfirmWindow(null);
    setSidecar(null);
    setSidecarError(null);
    await loadPreview(w);
  }

  function closePreview() {
    cancelCuration();
    curationGenerationRef.current += 1;
    previewWindowRef.current = null;
    editingPreviewRef.current = false;
    previewRequestRef.current += 1;
    setPreviewWindow(null);
    setRefileTarget(null);
    setConfirmWindow(null);
    setSidecar(null);
    setSidecarError(null);
    setRemovedItems(new Map());
    setSelectedAdditions(new Set());
    setCurationBusy(null);
    setCurationError(null);
  }

  useEffect(() => {
    const current = previewWindowRef.current;
    if (current === null) return;
    setSidecar(null);
    setSidecarError(null);
    // An outbox refresh is a new authoritative projection boundary. Undo rows
    // and in-flight continuations describe the prior projection and must not
    // survive into the refreshed one.
    curationGenerationRef.current += 1;
    setCurationBusy(null);
    setCurationError(null);
    setRemovedItems(new Map());
    setSelectedAdditions(new Set());
    void loadPreview(current);
    // Re-project only when delivery state changes. loadPreview is recreated every
    // render, so listing it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRefreshKey]);

  useEffect(() => {
    cancelCuration();
    curationGenerationRef.current += 1;
    detailRequestRef.current += 1;
    previewRequestRef.current += 1;
    previewWindowRef.current = null;
    editingPreviewRef.current = false;
    setDetailKey(null);
    setFiledDetailKey(null);
    setPreviewWindow(null);
    setRefileTarget(null);
    setSidecar(null);
    setSidecarError(null);
    setCurationBusy(null);
    setCurationError(null);
    return () => cancelCuration();
  }, [projectKey]);

  async function setItemIncluded(item: ReportIncludedItem, included: boolean) {
    const window = previewWindowRef.current;
    if (window === null || !onSetItemIncluded) return;
    const generation = curationGenerationRef.current;
    cancelCuration();
    const controller = new AbortController();
    curationAbortRef.current = controller;
    setConfirmWindow(null);
    setCurationBusy(occurrenceId(item));
    setCurationError(null);
    let succeeded = false;
    try {
      succeeded = await onSetItemIncluded(curationTargetId(item), window.report_kind, included, controller.signal, window, item);
    } catch {
      if (generation === curationGenerationRef.current) {
        setCurationError(`Couldn't ${included ? "restore" : "remove"} “${item.title}”.`);
        setCurationBusy(null);
      }
      return;
    } finally {
      if (curationAbortRef.current === controller) curationAbortRef.current = null;
    }
    if (generation !== curationGenerationRef.current) return;
    if (!succeeded) {
      setCurationError(`Couldn't ${included ? "restore" : "remove"} “${item.title}”.`);
      setCurationBusy(null);
      return;
    }
    setRemovedItems((current) => {
      const next = new Map(current);
      if (included) next.delete(occurrenceId(item));
      else next.set(occurrenceId(item), item);
      return next;
    });
    setSidecar(null);
    setSidecarError(null);
    await loadPreview(window);
    if (generation !== curationGenerationRef.current) return;
    setCurationBusy(null);
  }

  async function addSelectedItems() {
    const window = previewWindowRef.current;
    if (window === null || !onSetItemIncluded || !sidecar) return;
    const requested = (sidecar.eligible_additions ?? []).filter((item) => selectedAdditions.has(occurrenceId(item)));
    if (requested.length === 0) return;
    const generation = curationGenerationRef.current;
    cancelCuration();
    const controller = new AbortController();
    curationAbortRef.current = controller;
    setConfirmWindow(null);
    setCurationBusy("__additions__");
    setCurationError(null);
    const failed = new Set<string>();
    for (const item of requested) {
      if (controller.signal.aborted || generation !== curationGenerationRef.current) return;
      try {
        const ok = await onSetItemIncluded(curationTargetId(item), window.report_kind, true, controller.signal, window, item);
        if (!ok) failed.add(occurrenceId(item));
      } catch {
        failed.add(occurrenceId(item));
      }
    }
    if (curationAbortRef.current === controller) curationAbortRef.current = null;
    if (generation !== curationGenerationRef.current) return;
    // Keep only failures selected: successful rows disappear from the candidate
    // projection, while every failed row remains a truthful one-tap retry.
    setSelectedAdditions(failed);
    setSidecar(null);
    setSidecarError(null);
    await loadPreview(window);
    if (generation !== curationGenerationRef.current) return;
    setCurationBusy(null);
    if (failed.size > 0) setCurationError(`Couldn't add ${failed.size === 1 ? "the selected item" : `${failed.size} selected items`}. Retry the remaining selection.`);
  }

  async function doSend(entry: ReportEntry) {
    const bytes = await fetchPdfBytes(entry.pdf_key);
    onSend?.(entry, bytes);
    setConfirmOpen(false);
  }

  function onSendClick(entry: ReportEntry) {
    if (sidecar?.sent) return; // already sent — no re-send
    // §4 rule 7: anything that LEAVES THE COMPANY takes the second confirm —
    // client and weekly both. The GC-only daily goes straight through (§4 rule 8
    // — the single approve IS the human sign-off on an auto-drafted internal log).
    if (leavesTheCompany(entry.report_kind)) {
      setConfirmOpen(true);
    } else {
      void doSend(entry);
    }
  }

  async function openRefilePreview(report: FiledReport) {
    if (report.is_head === false) return;
    if (clientBlocked(report.kind)) {
      onComingSoon?.(CLIENT_REPORT_COMING_SOON);
      return;
    }
    const window = fileableWindowFromFiled(report);
    if (window === null) {
      onComingSoon?.(`Couldn't work out the date range for "${report.date_slug}".`);
      return;
    }
    await openPreview(window, report);
  }

  async function approvePreview(window: FileableWindow) {
    if (curationBusy !== null || curationError !== null || sidecar === null || sidecarError !== null || selectedAdditions.size > 0) return;
    if (refileTarget !== null) {
      const succeeded = (await onRefile?.(refileTarget)) === true;
      setConfirmWindow(null);
      if (!succeeded) return;
      setSidecar(null);
      setSidecarError(null);
      await loadPreview(window);
      return;
    }
    onFileWindow?.(window);
    setConfirmWindow(null);
    closePreview();
  }

  return {
    detail,
    confirmOpen,
    confirmWindow,
    setConfirmWindow,
    filter,
    setFilter,
    sidecar,
    sidecarError,
    setConfirmOpen,
    previewWindow,
    refileTarget,
    curationBusy,
    curationError,
    removedItems,
    selectedAdditions,
    setSelectedAdditions,
    rootRef,
    filtered,
    fileableInFilter,
    fileableShown,
    filedShown,
    filedDetail,
    openDetail,
    closeDetail,
    openFiledDetail,
    closeFiledDetail,
    openPreview,
    closePreview,
    setItemIncluded,
    addSelectedItems,
    doSend,
    onSendClick,
    openRefilePreview,
    approvePreview,
  };
}
